import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  AlertCircle, Wind, Heart, Brain, Zap, Phone, Shield, Sparkles,
  TreePine, Play, TrendingUp, Hand, Activity, Dumbbell, Mountain,
  Music, Sun, Waves
} from "lucide-react";
import { SparkleGuide } from '@/components/guide/SparkleGuide';
import BreathworkAnimation from "@/components/BreathworkAnimation";
import { GroundingExercise } from "./exercises/GroundingExercise";
import { TIPPExercise } from "./exercises/TIPPExercise";
import { useNavigate } from "react-router-dom";
import { useUniversalTracking } from "@/hooks/useUniversalTracking";

interface InTheMomentToolkitProps {
  user: any;
}

interface Tool {
  id: string;
  name: string;
  icon: any;
  time: string;
  description: string;
  urgent: boolean;
  link?: string;
  category: 'breathwork' | 'grounding' | 'movement' | 'mindfulness' | 'crisis' | 'sensory';
}

export const InTheMomentToolkit = ({ user }: InTheMomentToolkitProps) => {
  const navigate = useNavigate();
  const [mode, setMode] = useState<'quick' | 'support'>('quick');
  const [intensityLevel, setIntensityLevel] = useState([5]);
  const [activeExercise, setActiveExercise] = useState<string | null>(null);
  const [correlationInsight, setCorrelationInsight] = useState<string | null>(null);
  const { getPersonalizedRecommendation, getSymptomCorrelations } = useUniversalTracking();

  useEffect(() => {
    const loadInsight = async () => {
      const recommendation = await getPersonalizedRecommendation();
      setCorrelationInsight(recommendation);
    };
    loadInsight();
  }, []);

  const getIntensityLabel = (level: number) => {
    if (level <= 3) return { label: 'Mild', color: 'text-secondary', bg: 'bg-secondary/20 border border-secondary/30' };
    if (level <= 6) return { label: 'Moderate', color: 'text-primary-foreground', bg: 'bg-primary/90 border border-primary' };
    if (level <= 8) return { label: 'High', color: 'text-background', bg: 'bg-accent/90 border border-accent' };
    return { label: 'Crisis', color: 'text-foreground', bg: 'bg-destructive border border-destructive' };
  };

  const currentIntensity = getIntensityLabel(intensityLevel[0]);

  // Expanded toolkit with more activities
  const allTools: Tool[] = [
    // Breathwork
    { id: 'breath-478', name: '4-7-8 Breathing', icon: Wind, time: '2 min', description: 'Calm your nervous system', urgent: false, category: 'breathwork' },
    { id: 'box-breathing', name: 'Box Breathing', icon: Wind, time: '3 min', description: 'Focus and clarity', urgent: false, category: 'breathwork', link: '/breathwork' },
    { id: 'diaphragmatic', name: 'Belly Breathing', icon: Wind, time: '5 min', description: 'Deep relaxation', urgent: false, category: 'breathwork', link: '/breathwork' },
    
    // Grounding
    { id: '54321', name: '5-4-3-2-1 Grounding', icon: Zap, time: '3 min', description: 'Immediate grounding', urgent: false, category: 'grounding' },
    { id: 'body-scan', name: 'Quick Body Scan', icon: Heart, time: '3 min', description: 'Release physical tension', urgent: false, category: 'grounding' },
    { id: 'ice-dive', name: 'Ice Dive', icon: Waves, time: '30 sec', description: 'Activate diving reflex', urgent: false, category: 'sensory' },
    
    // Movement
    { id: 'walk', name: 'Mindful Walk', icon: TreePine, time: '10 min', description: 'Nature connection & movement', urgent: false, category: 'movement' },
    { id: 'yoga', name: 'Gentle Yoga', icon: Activity, time: '10 min', description: 'Stretch and release', urgent: false, category: 'movement' },
    { id: 'cardio', name: 'Quick Cardio Burst', icon: Zap, time: '5 min', description: 'Release nervous energy', urgent: false, category: 'movement' },
    { id: 'weights', name: 'Strength Training', icon: Dumbbell, time: '15 min', description: 'Channel energy physically', urgent: false, category: 'movement' },
    { id: 'intense-exercise', name: 'Intense Movement', icon: Mountain, time: '5 min', description: 'Full energy release', urgent: false, category: 'movement' },
    
    // Mindfulness
    { id: 'meditation', name: 'Guided Meditation', icon: Sparkles, time: '5 min', description: 'Meditation Medication®', urgent: false, category: 'mindfulness', link: '/meditations' },
    { id: 'mindful-moment', name: 'Mindful Moment', icon: Brain, time: '1 min', description: 'Anchor in the present', urgent: false, category: 'mindfulness' },
    { id: 'gratitude', name: 'Gratitude Reset', icon: Sun, time: '2 min', description: 'Shift your perspective', urgent: false, category: 'mindfulness' },
    { id: 'half-smile', name: 'Half Smile', icon: Heart, time: '2 min', description: 'Soften resistance', urgent: false, category: 'mindfulness' },
    
    // Therapeutic techniques
    { id: 'eft', name: 'EFT Tapping', icon: Hand, time: '5 min', description: 'Emotional Freedom Technique', urgent: false, category: 'sensory' },
    { id: 'tipp', name: 'TIPP Technique', icon: Zap, time: '5 min', description: 'Intense distress tolerance', urgent: false, category: 'sensory' },
    { id: 'opposite-action', name: 'Opposite Action', icon: Heart, time: '10 min', description: 'Change emotional state', urgent: false, category: 'mindfulness' },
    { id: 'radical-acceptance', name: 'Radical Acceptance', icon: Brain, time: '5 min', description: 'Accept reality', urgent: false, category: 'mindfulness' },
    
    // Sensory
    { id: 'music', name: 'Calming Music', icon: Music, time: '5 min', description: 'Soothing soundscape', urgent: false, category: 'sensory' },
    
    // Crisis
    { id: 'crisis-helpline', name: 'Call Crisis Line', icon: Phone, time: 'Now', description: 'Speak to someone immediately', urgent: true, category: 'crisis' },
    { id: 'crisis-protocol', name: 'Crisis Protocol', icon: AlertCircle, time: 'Now', description: 'Emergency safety steps', urgent: true, category: 'crisis' },
    { id: 'safety-plan', name: 'My Safety Plan', icon: Shield, time: 'Now', description: 'Personal crisis plan', urgent: true, category: 'crisis' },
  ];

  const getToolsForIntensity = (): Tool[] => {
    const level = intensityLevel[0];
    if (level <= 3) {
      // Mild - focus on preventive maintenance
      return allTools.filter(t => 
        ['breathwork', 'mindfulness', 'movement'].includes(t.category) && !t.urgent
      ).slice(0, 8);
    }
    if (level <= 6) {
      // Moderate - grounding and regulation
      return allTools.filter(t => 
        ['grounding', 'breathwork', 'sensory', 'mindfulness'].includes(t.category) && !t.urgent
      ).slice(0, 8);
    }
    if (level <= 8) {
      // High - intensive techniques
      return allTools.filter(t => 
        ['sensory', 'grounding', 'movement'].includes(t.category) && !t.urgent
      ).slice(0, 8);
    }
    // Crisis
    return allTools.filter(t => t.category === 'crisis' || t.id === 'tipp' || t.id === 'ice-dive');
  };

  const getQuickPracticeTools = (): Tool[] => {
    // For daily wellness - variety of tools without distress framing
    return allTools.filter(t => !t.urgent).slice(0, 12);
  };

  if (activeExercise === 'breath-478') {
    return (
      <div>
        <Button 
          onClick={() => setActiveExercise(null)}
          variant="outline"
          className="mb-6"
        >
          ← Back to Toolkit
        </Button>
        <Card className="bg-gradient-calm">
          <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wind className="h-6 w-6 text-primary" />
            4-7-8 Breathing
          </CardTitle>
          </CardHeader>
          <CardContent>
            <BreathworkAnimation />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (activeExercise === '54321') {
    return (
      <div>
        <Button 
          onClick={() => setActiveExercise(null)}
          variant="outline"
          className="mb-6"
        >
          ← Back to Toolkit
        </Button>
        <GroundingExercise onComplete={() => setActiveExercise(null)} />
      </div>
    );
  }

  if (activeExercise === 'tipp') {
    return (
      <div>
        <Button 
          onClick={() => setActiveExercise(null)}
          variant="outline"
          className="mb-6"
        >
          ← Back to Toolkit
        </Button>
        <TIPPExercise />
      </div>
    );
  }

  const renderToolCard = (tool: Tool) => {
    const Icon = tool.icon;
    return (
      <Card 
        key={tool.id}
        className={`hover:shadow-therapy transition-all duration-300 cursor-pointer ${
          tool.urgent ? 'border-destructive/50 bg-destructive/10' : 'border-border/50'
        }`}
        onClick={() => {
          if (tool.link) {
            navigate(tool.link);
          } else {
            setActiveExercise(tool.id);
          }
        }}
      >
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3 flex-1">
              <div className={`p-2.5 rounded-lg ${tool.urgent ? 'bg-destructive/20' : 'bg-primary/10'}`}>
                <Icon className={`h-5 w-5 ${tool.urgent ? 'text-destructive' : 'text-primary'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <CardTitle className="text-base mb-0.5">{tool.name}</CardTitle>
                <CardDescription className="text-xs">{tool.description}</CardDescription>
              </div>
            </div>
            <Badge variant="secondary" className="text-xs ml-2 shrink-0">
              {tool.time}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="pt-2">
          <Button 
            variant={tool.urgent ? "destructive" : "outline"}
            size="sm" 
            className="w-full text-sm"
          >
            {tool.urgent ? 'Access Now' : 'Start'}
          </Button>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <div className="flex items-center justify-center gap-2 mb-2">
          <h3 className="text-2xl font-light text-foreground">In-the-Moment Toolkit</h3>
          <SparkleGuide
            title="How to use the Toolkit"
            items={[
              { title: 'Quick Practice', description: 'Daily wellness tools for prevention - practice regularly to build resilience.' },
              { title: 'Need Support', description: 'Use the intensity slider to get tools matched to your current distress level.' },
              { title: 'Track what works', description: 'Over time, you\'ll see which tools are most effective for you personally.' },
              { title: 'Crisis support', description: 'At high distress levels, crisis resources become available immediately.' }
            ]}
          />
        </div>
        <p className="text-sm text-muted-foreground max-w-2xl mx-auto leading-relaxed">
          Evidence-based tools for immediate support and daily wellness practice
        </p>
      </div>

      {/* Mode Toggle */}
      <Tabs value={mode} onValueChange={(v) => setMode(v as 'quick' | 'support')} className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-4">
          <TabsTrigger value="quick" className="flex items-center gap-2">
            <Play className="h-4 w-4" />
            Quick Practice
          </TabsTrigger>
          <TabsTrigger value="support" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Need Support
          </TabsTrigger>
        </TabsList>

        {/* Correlation Insight Card */}
        {correlationInsight && (
          <Card className="mb-4 border-secondary/30 bg-secondary/5">
            <CardContent className="py-3">
              <div className="flex items-center gap-3">
                <TrendingUp className="h-5 w-5 text-secondary shrink-0" />
                <p className="text-sm text-foreground">{correlationInsight}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Practice Mode */}
        <TabsContent value="quick" className="space-y-4">
          <Card className="bg-gradient-calm border-border/50">
            <CardContent className="py-4">
              <p className="text-sm text-muted-foreground text-center">
                Daily wellness practices to build resilience. No tracking required - just practice.
              </p>
            </CardContent>
          </Card>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {getQuickPracticeTools().map(renderToolCard)}
          </div>
        </TabsContent>

        {/* Support Mode with Intensity */}
        <TabsContent value="support" className="space-y-4">
          {/* Intensity Selector */}
          <Card className="bg-gradient-therapeutic border-primary/20">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-3 text-lg">
                <AlertCircle className="h-5 w-5 text-primary" />
                How are you feeling right now?
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Distress Level</span>
                  <Badge className={`${currentIntensity.bg} ${currentIntensity.color} border-0 text-sm`}>
                    {currentIntensity.label}
                  </Badge>
                </div>
                <Slider
                  value={intensityLevel}
                  onValueChange={setIntensityLevel}
                  min={1}
                  max={10}
                  step={1}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Calm</span>
                  <span>Moderate</span>
                  <span>Crisis</span>
                </div>
              </div>

              <div className="bg-background/50 rounded-lg p-3 space-y-1">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {intensityLevel[0] <= 3 && "Mild distress - these tools help maintain balance and prevent escalation."}
                  {intensityLevel[0] > 3 && intensityLevel[0] <= 6 && "Moderate distress - these tools help you regulate and ground yourself."}
                  {intensityLevel[0] > 6 && intensityLevel[0] <= 8 && "High distress - intensive techniques to help you through this difficult moment."}
                  {intensityLevel[0] > 8 && "Crisis level - prioritise your safety. Consider reaching out for support."}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Crisis Warning */}
          {intensityLevel[0] > 8 && (
            <Card className="border-destructive/50 bg-destructive/10">
              <CardContent className="pt-4 pb-3">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-destructive mt-0.5 flex-shrink-0" />
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-foreground">Crisis Support Resources</p>
                    <div className="space-y-1 text-xs text-foreground">
                      <p>🇬🇧 <strong>UK:</strong> Samaritans 116 123 (24/7, free)</p>
                      <p>🇺🇸 <strong>US:</strong> 988 Suicide & Crisis Lifeline</p>
                      <p>🚨 <strong>Emergency:</strong> 999 (UK) or 911 (US)</p>
                    </div>
                    <Button size="sm" variant="destructive" className="mt-2" onClick={() => navigate('/crisis')}>
                      Open Crisis Suite
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recommended Tools for intensity */}
          <div className="grid gap-3 sm:grid-cols-2">
            {getToolsForIntensity().map(renderToolCard)}
          </div>
        </TabsContent>
      </Tabs>

      {/* Additional Support */}
      <Card className="bg-gradient-calm border-border/50">
        <CardContent className="py-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-3 text-center sm:text-left">
              <Shield className="h-5 w-5 text-primary shrink-0" />
              <div>
                <p className="text-sm font-medium">Need More Support?</p>
                <p className="text-xs text-muted-foreground">
                  Access your safety plan or connect with AI Therappi
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => navigate('/crisis')}>
                Crisis Suite
              </Button>
              <Button variant="default" size="sm" onClick={() => navigate('/therapy')}>
                AI Therappi
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
